import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/api/client';
import type {
  EmailTemplate,
  LeadList,
  SequenceSummary,
  SuppressionRow,
  TemplateKpiRow,
} from '@/api/types';
import {
  blocksToPlainText,
  blueprintSubject,
  CAMPAIGN_TYPE_LABELS,
  CAMPAIGN_TYPES,
  cloneBlueprintBlocks,
  emailTemplateBlocksSchema,
  suggestedInternalTemplateName,
  validateTemplatePhase1,
  assessBrandDnaForTemplateAi,
  scoreEmailTemplateContent,
  validateTemplateGovernance,
  parseTagsInput,
  formatTagsForInput,
  suggestedDefaultTags,
  type EmailTemplateBlock,
  type TemplateCategory,
} from '@marketing-hub/shared';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextInput, TextArea } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { CampaignQuickSendPanel } from '@/features/campaigns/CampaignQuickSendPanel';
import { formatPct } from '@/features/campaigns/campaignUi';
import campStyles from '@/features/campaigns/Campaigns.module.css';
import { EmailTemplateBlockEditor } from '@/features/emailTemplates/EmailTemplateBlockEditor';
import {
  EmailTemplateHtmlEditor,
  type EnhanceHtmlOptions,
} from '@/features/emailTemplates/EmailTemplateHtmlEditor';
import { EmailTemplatePreviewPane } from '@/features/emailTemplates/EmailTemplatePreviewPane';
import { EmailTemplateVariablesPanel } from '@/features/emailTemplates/EmailTemplateVariablesPanel';
import {
  defaultStarterBlocks,
  updateBlockAt,
} from '@/features/emailTemplates/emailTemplateBlockTypes';
import { EmailTemplateAiWizard, type AiWizardOptions } from '@/features/emailTemplates/EmailTemplateAiWizard';
import { EmailTemplateScoreChips } from '@/features/emailTemplates/EmailTemplateScoreChips';
import { TemplateStructurePanel } from '@/features/emailTemplates/TemplateStructurePanel';
import { TemplateGovernanceBanner } from '@/features/emailTemplates/TemplateGovernanceBanner';
import {
  aggregateTemplateKpi,
  EmailTemplatePerformancePanel,
} from '@/features/emailTemplates/EmailTemplatePerformancePanel';
import { useToast } from '@/components/toast/ToastProvider';
import leadStyles from '@/features/leads/Leads.module.css';
import { ResizableWorkspace } from '@/components/workspace/ResizableWorkspace';
import { SequenceLibraryWorkspace } from '@/features/sequences/SequenceLibraryWorkspace';
import { PreviewLeadPicker } from '@/features/emailTemplates/PreviewLeadPicker';
import styles from '../ProjectsPage.module.css';

export function CampaignLibraryPage() {
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [listName, setListName] = useState('');
  const [seqName, setSeqName] = useState('');
  const [seqSubject, setSeqSubject] = useState('{{voornaam}}, korte vraag');
  const [seqBody, setSeqBody] = useState(
    'Hoi {{voornaam}},\n\nIk ben benieuwd of {{company}} openstaat voor…\n\nGroet',
  );
  const [seqStep0TemplateId, setSeqStep0TemplateId] = useState('');
  const [campaignType, setCampaignType] =
    useState<(typeof CAMPAIGN_TYPES)[number]>('cold_outreach');

  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [tplName, setTplName] = useState('');
  const [tplSubject, setTplSubject] = useState(() => blueprintSubject('cold_outreach'));
  const [tplBody, setTplBody] = useState(() =>
    blocksToPlainText(cloneBlueprintBlocks('cold_outreach')),
  );
  const [tplBlocks, setTplBlocks] = useState<EmailTemplateBlock[]>(() =>
    defaultStarterBlocks('cold_outreach'),
  );
  const [tplUseLegacyHtml, setTplUseLegacyHtml] = useState(false);
  const [tplHtml, setTplHtml] = useState('');
  const [tplAiWarning, setTplAiWarning] = useState<string | null>(null);
  const [editorView, setEditorView] = useState<'edit' | 'preview'>('edit');
  const [previewViewport, setPreviewViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [searchParams, setSearchParams] = useSearchParams();
  const [tplSubjectChoices, setTplSubjectChoices] = useState<string[]>([]);
  const [previewLeadId, setPreviewLeadId] = useState('');
  const [focusedBlock, setFocusedBlock] = useState<{
    index: number;
    field: 'text' | 'label' | 'url_template';
  } | null>(null);
  const [improvingBlockIndex, setImprovingBlockIndex] = useState<number | null>(null);
  const [tplTagsInput, setTplTagsInput] = useState('');
  const [templateTagFilter, setTemplateTagFilter] = useState('');
  const [showArchivedTemplates, setShowArchivedTemplates] = useState(false);

  function applyBlueprintToForm(category: TemplateCategory) {
    const blocks = cloneBlueprintBlocks(category);
    setTplSubject(blueprintSubject(category));
    setTplBlocks(blocks);
    setTplBody(blocksToPlainText(blocks));
    setTplUseLegacyHtml(false);
    setTplHtml('');
    setTplTagsInput(formatTagsForInput(suggestedDefaultTags(category)));
  }

  function resetTemplateForm() {
    setEditingTemplateId(null);
    setTplName('');
    applyBlueprintToForm(campaignType);
    setTplAiWarning(null);
    setCreatingNewTemplate(false);
    setEditorView('edit');
    setTplTagsInput('');
  }

  function handleCampaignTypeChange(next: TemplateCategory) {
    if (next === campaignType) return;
    const isNew = creatingNewTemplate && !editingTemplateId;
    if (!isNew && editorOpen && !tplUseLegacyHtml) {
      const ok = window.confirm(
        'Bij een ander campagnetype worden onderwerp en blokken vervangen door het standaardsjabloon. Doorgaan?',
      );
      if (!ok) return;
    }
    setCampaignType(next);
    if (!tplUseLegacyHtml && editorOpen) {
      applyBlueprintToForm(next);
      if (isNew) {
        setTplName(suggestedInternalTemplateName(selectedProject?.name ?? '', next));
      }
    }
  }

  function loadTemplateIntoForm(t: EmailTemplate) {
    setEditorView('edit');
    setEditingTemplateId(t.id);
    setTplName(t.name);
    setTplSubject(t.subject_template);
    setTplBody(t.body_text_template);
    setTplTagsInput(formatTagsForInput(t.tags ?? []));
    setTplAiWarning(null);
    if (CAMPAIGN_TYPES.includes(t.category as TemplateCategory)) {
      setCampaignType(t.category as TemplateCategory);
    }
    if (t.render_mode === 'blocks' && t.body_blocks) {
      const parsed = emailTemplateBlocksSchema.safeParse(t.body_blocks);
      if (parsed.success) {
        setTplBlocks(parsed.data);
        setTplUseLegacyHtml(false);
        setTplHtml('');
        return;
      }
    }
    if (t.render_mode === 'html' && t.body_html) {
      setTplUseLegacyHtml(true);
      setTplHtml(t.body_html);
    } else {
      setTplUseLegacyHtml(false);
      const cat = CAMPAIGN_TYPES.includes(t.category as TemplateCategory)
        ? (t.category as TemplateCategory)
        : 'cold_outreach';
      setTplBlocks(defaultStarterBlocks(cat));
    }
  }

  function buildTemplatePayload(): Record<string, unknown> | null {
    if (tplUseLegacyHtml && tplHtml.trim()) {
      return {
        project_id: projectId,
        name: tplName,
        category: campaignType,
        subject_template: tplSubject,
        body_text_template: tplBody,
        body_html: tplHtml.trim(),
        render_mode: 'html',
      };
    }
    const sanitized = tplBlocks.filter((b) => {
      if (b.type === 'divider') return true;
      if (b.type === 'paragraph' || b.type === 'heading') return b.text.trim().length > 0;
      return b.label.trim().length > 0 && b.url_template.trim().length > 0;
    });
    const parsed = emailTemplateBlocksSchema.safeParse(sanitized);
    if (!parsed.success) {
      return null;
    }
    const bodyText = blocksToPlainText(parsed.data);
    return {
      project_id: projectId,
      name: tplName,
      category: campaignType,
      subject_template: tplSubject,
      body_text_template: bodyText,
      body_blocks: parsed.data,
      render_mode: 'blocks',
      tags: parseTagsInput(tplTagsInput),
    };
  }

  const [suppEmail, setSuppEmail] = useState('');
  const [suppDomain, setSuppDomain] = useState('');
  const [libraryTab, setLibraryTab] = useState<
    'templates' | 'sequences' | 'lists' | 'suppressions'
  >('templates');
  const [quickSendTemplateId, setQuickSendTemplateId] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<'all' | (typeof CAMPAIGN_TYPES)[number]>(
    'all',
  );
  const [creatingNewTemplate, setCreatingNewTemplate] = useState(false);
  const [quickSendOpen, setQuickSendOpen] = useState(false);

  useEffect(() => {
    if (quickSendTemplateId) setQuickSendOpen(true);
  }, [quickSendTemplateId]);

  const listsQ = useQuery({
    queryKey: ['lists', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<{ lists: LeadList[] }>(`/api/lists?project_id=${projectId}`),
  });

  const seqQ = useQuery({
    queryKey: ['sequences', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ sequences: SequenceSummary[] }>(`/api/sequences?project_id=${projectId}`),
  });

  const templatesQ = useQuery({
    queryKey: ['email-templates', projectId, showArchivedTemplates, templateTagFilter],
    enabled: Boolean(projectId),
    queryFn: () => {
      const params = new URLSearchParams({ project_id: projectId! });
      if (showArchivedTemplates) params.set('include_archived', '1');
      if (templateTagFilter.trim()) params.set('tag', templateTagFilter.trim());
      return apiFetch<{ templates: EmailTemplate[] }>(`/api/email-templates?${params.toString()}`);
    },
  });

  const templates = templatesQ.data?.templates ?? [];

  const templateKpiQ = useQuery({
    queryKey: ['email-template-kpi', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ templateKpi: TemplateKpiRow[] }>(
        `/api/email-templates/kpi?project_id=${encodeURIComponent(projectId!)}`,
      ),
  });
  const templateKpiById = useMemo(
    () => aggregateTemplateKpi(templateKpiQ.data?.templateKpi ?? []),
    [templateKpiQ.data],
  );

  const commonTemplateTags = useMemo(() => {
    const set = new Set<string>();
    for (const t of templates) {
      for (const tag of t.tags ?? []) set.add(tag);
    }
    return [...set].sort();
  }, [templates]);
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return templates.filter((t) => {
      if (templateCategoryFilter !== 'all' && t.category !== templateCategoryFilter) return false;
      if (!q) return true;
      const cat =
        CAMPAIGN_TYPE_LABELS[t.category as keyof typeof CAMPAIGN_TYPE_LABELS] ?? t.category;
      return (
        t.name.toLowerCase().includes(q) ||
        t.subject_template.toLowerCase().includes(q) ||
        cat.toLowerCase().includes(q)
      );
    });
  }, [templates, templateSearch, templateCategoryFilter]);

  const templateStats = useMemo(() => {
    let blocks = 0;
    let html = 0;
    let text = 0;
    for (const t of templates) {
      if (t.render_mode === 'blocks') blocks++;
      else if (t.render_mode === 'html') html++;
      else text++;
    }
    return { total: templates.length, blocks, html, text };
  }, [templates]);

  function startNewTemplate() {
    resetTemplateForm();
    setCreatingNewTemplate(true);
    setEditingTemplateId(null);
    const name = suggestedInternalTemplateName(selectedProject?.name ?? '', campaignType);
    setTplName(name);
    applyBlueprintToForm(campaignType);
  }

  function closeTemplateEditor() {
    resetTemplateForm();
    setCreatingNewTemplate(false);
  }

  function openTemplateEditor(t: EmailTemplate) {
    setCreatingNewTemplate(false);
    loadTemplateIntoForm(t);
  }

  useEffect(() => {
    const tid = searchParams.get('template');
    if (!tid || templatesQ.isLoading) return;
    const clearTemplateParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('template');
          return next;
        },
        { replace: true },
      );
    };
    const t = templates.find((x) => x.id === tid);
    if (!t) {
      clearTemplateParam();
      return;
    }
    openTemplateEditor(t);
    clearTemplateParam();
  }, [searchParams, templates, templatesQ.isLoading, setSearchParams]);

  const editorOpen = creatingNewTemplate || Boolean(editingTemplateId);

  const editingTemplate = useMemo(
    () => templates.find((t) => t.id === editingTemplateId),
    [templates, editingTemplateId],
  );

  const brandDnaQ = useQuery({
    queryKey: ['brand-dna', projectId],
    enabled: Boolean(projectId) && editorOpen,
    queryFn: () =>
      apiFetch<{
        brand_dna: {
          tone_of_voice?: string | null;
          writing_style?: string | null;
          target_audience_summary?: string | null;
          value_props?: string[] | null;
        } | null;
      }>(`/api/projects/${projectId}/brand-dna`),
  });

  const dnaReadiness = useMemo(
    () => assessBrandDnaForTemplateAi(brandDnaQ.data?.brand_dna ?? null),
    [brandDnaQ.data?.brand_dna],
  );

  const phase1Validation = useMemo(() => {
    if (tplUseLegacyHtml) return { ok: true, warnings: [] as string[] };
    return validateTemplatePhase1({ subject: tplSubject, blocks: tplBlocks });
  }, [tplSubject, tplBlocks, tplUseLegacyHtml]);

  const governanceValidation = useMemo(() => {
    if (tplUseLegacyHtml) {
      return validateTemplateGovernance({
        name: tplName,
        subject: tplSubject,
        body_text: tplBody,
        render_mode: 'html',
      });
    }
    return validateTemplateGovernance({
      name: tplName,
      subject: tplSubject,
      blocks: tplBlocks,
      render_mode: 'blocks',
    });
  }, [tplName, tplSubject, tplBlocks, tplBody, tplUseLegacyHtml]);

  const contentScores = useMemo(() => {
    if (tplUseLegacyHtml) return null;
    return scoreEmailTemplateContent({
      subject: tplSubject,
      blocks: tplBlocks,
      category: campaignType,
    });
  }, [tplSubject, tplBlocks, tplUseLegacyHtml, campaignType]);

  const templateVariableContent = useMemo(
    () => ({
      subject_template: tplSubject,
      body_text_template: tplBody,
      body_html: tplHtml,
      body_blocks: tplBlocks,
      render_mode: tplUseLegacyHtml ? ('html' as const) : ('blocks' as const),
    }),
    [tplSubject, tplBody, tplHtml, tplBlocks, tplUseLegacyHtml],
  );

  const improveBlockAi = useMutation({
    mutationFn: async (index: number) => {
      const block = tplBlocks[index];
      if (!block) throw new Error('Geen blok');
      return apiFetch<{ block: EmailTemplateBlock }>('/api/email-templates/improve-block', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          campaign_type: campaignType,
          tone: 'friendly',
          block,
          subject_template: tplSubject,
        }),
      });
    },
    onMutate: (index) => setImprovingBlockIndex(index),
    onSettled: () => setImprovingBlockIndex(null),
    onSuccess: (data, index) => {
      setTplBlocks((prev) => {
        const next = updateBlockAt(prev, index, data.block);
        try {
          setTplBody(blocksToPlainText(next));
        } catch {
          /* incomplete */
        }
        return next;
      });
    },
  });

  function runAiFromWizard(opts: AiWizardOptions) {
    let skipDnaCheck = false;
    if (!dnaReadiness.ok) {
      const go = window.confirm(
        `Bedrijfs-DNA is nog niet compleet (${dnaReadiness.missing.join(', ')}). Toch genereren?`,
      );
      if (!go) return;
      skipDnaCheck = true;
    }
    aiTplFromDna.mutate({ skipDnaCheck, ...opts });
  }

  function renderModeBadge(mode: string | undefined) {
    const m = mode ?? 'text';
    const cls =
      m === 'blocks'
        ? campStyles.modeBadge_blocks
        : m === 'html'
          ? campStyles.modeBadge_html
          : '';
    return <span className={`${campStyles.modeBadge} ${cls}`}>{m}</span>;
  }

  const suppressionsQ = useQuery({
    queryKey: ['suppressions', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ suppressions: SuppressionRow[] }>(`/api/suppressions?project_id=${projectId}`),
  });

  const leadsQ = useQuery({
    queryKey: ['leads-for-list', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ leads: { id: string; email: string | null }[] }>(
        `/api/leads?project_id=${projectId}&page=1&page_size=100`,
      ),
  });

  const sampleLeadId = previewLeadId || (leadsQ.data?.leads ?? []).find((l) => l.email)?.id;

  function buildInboxTestPayload(): Record<string, unknown> | null {
    if (!projectId || !tplSubject.trim()) return null;
    if (tplUseLegacyHtml) {
      if (!tplHtml.trim()) return null;
      return {
        project_id: projectId,
        lead_id: sampleLeadId,
        subject_template: tplSubject,
        body_text_template: tplBody,
        body_html: tplHtml.trim(),
        render_mode: 'html',
      };
    }
    const payload = buildTemplatePayload();
    if (!payload || payload.render_mode !== 'blocks') return null;
    return {
      project_id: projectId,
      lead_id: sampleLeadId,
      subject_template: tplSubject,
      body_text_template: payload.body_text_template,
      body_blocks: payload.body_blocks,
      render_mode: 'blocks',
    };
  }

  const inboxTestReady = useMemo(() => buildInboxTestPayload() != null, [
    projectId,
    tplSubject,
    tplUseLegacyHtml,
    tplHtml,
    tplBlocks,
    tplName,
    tplBody,
    campaignType,
    tplTagsInput,
    sampleLeadId,
  ]);

  const inboxTestSend = useMutation({
    mutationFn: () => {
      const body = buildInboxTestPayload();
      if (!body) throw new Error('Template incompleet voor testmail');
      return apiFetch<{ messageId: string; to: string }>('/api/email-templates/inbox-test', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      toast.push(`Testmail in wachtrij naar ${data.to}`, 'success');
    },
  });

  const enhanceHtmlAi = useMutation({
    mutationFn: (opts: EnhanceHtmlOptions) =>
      apiFetch<{
        body_html: string;
        body_text_template: string;
        subject_template?: string;
        notes?: string;
        variables_used: string[];
        enhancements_applied?: string[];
        model: string;
      }>('/api/email-templates/enhance-html', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          campaign_type: campaignType,
          tone: 'friendly',
          subject_template: tplSubject,
          body_html: tplHtml,
          body_text_template: tplBody,
          personalise: opts.personalise,
          apply_brand_styling: opts.apply_brand_styling,
          skip_dna_check: opts.skip_dna_check ?? false,
        }),
      }),
    onSuccess: (data) => {
      setTplHtml(data.body_html);
      setTplBody(data.body_text_template);
      if (data.subject_template?.trim()) setTplSubject(data.subject_template);
      const applied = (data.enhancements_applied ?? []).slice(0, 4).join(' · ');
      const vars = data.variables_used.length
        ? data.variables_used.map((k) => `{{${k}}}`).join(', ')
        : 'geen';
      const note = data.notes?.trim();
      toast.push(
        [
          `HTML bijgewerkt — variabelen: ${vars}`,
          applied ? applied : null,
          note ?? null,
        ]
          .filter(Boolean)
          .join(' · '),
        'success',
      );
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.push(err.message, 'error');
      }
    },
  });

  function insertTemplateVariable(token: string) {
    if (focusedBlock && !tplUseLegacyHtml) {
      setTplBlocks((prev) => {
        const block = prev[focusedBlock.index];
        if (!block) return prev;
        if (
          focusedBlock.field === 'text' &&
          (block.type === 'paragraph' || block.type === 'heading')
        ) {
          return updateBlockAt(prev, focusedBlock.index, { ...block, text: block.text + token });
        }
        if (focusedBlock.field === 'label' && block.type === 'cta') {
          return updateBlockAt(prev, focusedBlock.index, { ...block, label: block.label + token });
        }
        if (focusedBlock.field === 'url_template' && block.type === 'cta') {
          return updateBlockAt(prev, focusedBlock.index, {
            ...block,
            url_template: block.url_template + token,
          });
        }
        return prev;
      });
      return;
    }
    setTplSubject((s) => s + token);
  }

  const aiSeqOpening = useMutation({
    mutationFn: () =>
      apiFetch<{ suggestion: string }>('/api/ai/suggest-opening', {
        method: 'POST',
        body: JSON.stringify({ lead_id: sampleLeadId, tone: 'friendly' }),
      }),
    onSuccess: (data) => {
      setSeqBody((prev) =>
        prev.trim() ? `${data.suggestion}\n\n${prev}` : `${data.suggestion}\n\nGroet`,
      );
    },
  });

  const aiTplFromDna = useMutation({
    mutationFn: (input: { skipDnaCheck: boolean } & AiWizardOptions) =>
      apiFetch<{
        draft: {
          subject_template: string;
          body_text_template: string;
          body_blocks: unknown;
          render_mode: string;
        };
        validation: { ok: boolean; errors: string[] };
      }>('/api/email-templates/generate', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          campaign_type: campaignType,
          tone: input.tone,
          goal: input.goal,
          length: input.length,
          cta_style: input.cta_style,
          user_brief: input.user_brief,
          key_message: input.key_message,
          cta_url: input.cta_url,
          calendar_link: input.calendar_link,
          name: tplName.trim() || undefined,
          persist: false,
          skip_dna_check: input.skipDnaCheck,
        }),
      }),
    onSuccess: (data) => {
      setTplSubject(data.draft.subject_template);
      setTplBody(data.draft.body_text_template);
      const parsed = emailTemplateBlocksSchema.safeParse(data.draft.body_blocks);
      if (parsed.success) {
        setTplBlocks(parsed.data);
        setTplBody(data.draft.body_text_template);
        setTplUseLegacyHtml(false);
        setTplHtml('');
      }
      setTplAiWarning(
        data.validation.ok ? null : data.validation.errors.join(' · '),
      );
      if (!tplName.trim()) {
        setTplName(`AI — ${CAMPAIGN_TYPE_LABELS[campaignType]}`);
      }
    },
  });

  const aiTplSubjects = useMutation({
    mutationFn: () =>
      apiFetch<{ subjects: string[] }>('/api/ai/suggest-subjects', {
        method: 'POST',
        body: JSON.stringify({
          lead_id: sampleLeadId,
          tone: 'friendly',
          context: tplName || CAMPAIGN_TYPE_LABELS[campaignType],
          campaign_type: campaignType,
          count: 2,
        }),
      }),
    onSuccess: (data) => {
      const choices = data.subjects.filter(Boolean).slice(0, 2);
      setTplSubjectChoices(choices);
      if (choices[0]) setTplSubject(choices[0]);
    },
  });

  const createList = useMutation({
    mutationFn: async () => {
      const res = await apiFetch<{ list: LeadList }>('/api/lists', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, name: listName }),
      });
      const ids = (leadsQ.data?.leads ?? []).filter((l) => l.email).map((l) => l.id);
      if (ids.length) {
        await apiFetch(`/api/lists/${res.list.id}/members`, {
          method: 'POST',
          body: JSON.stringify({ lead_ids: ids }),
        });
      }
      return res;
    },
    onSuccess: () => {
      setListName('');
      void qc.invalidateQueries({ queryKey: ['lists', projectId] });
    },
  });

  const createSequence = useMutation({
    mutationFn: () =>
      apiFetch('/api/sequences', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          name: seqName,
          steps: [
            {
              step_order: 0,
              wait_days: 0,
              ...(seqStep0TemplateId
                ? { email_template_id: seqStep0TemplateId }
                : {
                    subject_template: seqSubject,
                    body_text_template: seqBody,
                  }),
            },
            {
              step_order: 1,
              wait_days: campaignType === 'broadcast' ? 0 : 3,
              subject_template: 'Re: {{voornaam}}',
              body_text_template: 'Even follow-up op mijn vorige mail…',
            },
          ],
        }),
      }),
    onSuccess: () => {
      setSeqName('');
      void qc.invalidateQueries({ queryKey: ['sequences', projectId] });
    },
  });

  const migrateLegacyTemplate = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ template: EmailTemplate }>(`/api/email-templates/${id}/migrate-blocks`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: (data) => {
      loadTemplateIntoForm(data.template);
      void qc.invalidateQueries({ queryKey: ['email-templates', projectId] });
      toast.push('Legacy template omgezet naar blokken.', 'success');
    },
  });

  const archiveTemplate = useMutation({
    mutationFn: (archived: boolean) => {
      if (!editingTemplateId) throw new Error('Geen template');
      return apiFetch(`/api/email-templates/${editingTemplateId}`, {
        method: 'PATCH',
        body: JSON.stringify({ archived }),
      });
    },
    onSuccess: () => {
      closeTemplateEditor();
      void qc.invalidateQueries({ queryKey: ['email-templates', projectId] });
    },
  });

  const saveTemplate = useMutation({
    mutationFn: async () => {
      if (!tplUseLegacyHtml && !phase1Validation.ok) {
        throw new Error(phase1Validation.warnings.join(' '));
      }
      if (!governanceValidation.ok) {
        throw new Error(governanceValidation.blockers.join(' '));
      }
      const payload = buildTemplatePayload();
      if (!payload) throw new Error('Vul minstens één geldig blok in.');
      if (editingTemplateId) {
        const { project_id: _p, ...patch } = payload;
        return apiFetch(`/api/email-templates/${editingTemplateId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
      }
      return apiFetch('/api/email-templates', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      resetTemplateForm();
      void qc.invalidateQueries({ queryKey: ['email-templates', projectId] });
    },
  });

  const addSuppression = useMutation({
    mutationFn: () =>
      apiFetch('/api/suppressions', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          email: suppEmail.trim() || null,
          domain: suppDomain.trim() || null,
          reason: 'manual',
        }),
      }),
    onSuccess: () => {
      setSuppEmail('');
      setSuppDomain('');
      void qc.invalidateQueries({ queryKey: ['suppressions', projectId] });
    },
  });

  if (!projectId) return null;
  if (listsQ.isLoading || seqQ.isLoading) return <LoadingState />;

  const tabCounts = {
    templates: templates.length,
    sequences: (seqQ.data?.sequences ?? []).length,
    lists: (listsQ.data?.lists ?? []).length,
    suppressions: (suppressionsQ.data?.suppressions ?? []).length,
  };

  const libraryFocus = libraryTab === 'templates' && editorOpen;

  return (
    <div
      className={[
        campStyles.libraryWorkspace,
        libraryFocus ? campStyles.libraryWorkspaceFocus : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <nav className={campStyles.subNav} aria-label="Bibliotheek secties">
        {(
          [
            ['templates', 'E-mailtemplates'],
            ['sequences', 'Sequences'],
            ['lists', 'Lijsten'],
            ['suppressions', 'Suppressies'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={[
              campStyles.subNavLink,
              libraryTab === id ? campStyles.subNavLinkActive : '',
            ].join(' ')}
            onClick={() => setLibraryTab(id)}
          >
            {label}
            <span className={campStyles.libraryTabCount}>{tabCounts[id]}</span>
          </button>
        ))}
      </nav>

      {libraryTab === 'lists' ? (
        <section className={campStyles.panel}>
          <div className={campStyles.panelHead}>
            <h2 className={campStyles.panelTitle}>Lijsten</h2>
            <span className={campStyles.metaChip}>Statische lead-verzamelingen</span>
          </div>
          <div className={campStyles.panelBody}>
            <form
              className={styles.formGrid}
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                createList.mutate();
              }}
            >
              <Field label="Nieuwe lijst">
                <TextInput value={listName} onChange={(e) => setListName(e.target.value)} required />
              </Field>
              <Button type="submit">Aanmaken + huidige leads (max 100)</Button>
            </form>
            <div className={campStyles.tableWrap}>
              <table className={campStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Naam</th>
                    <th>Contacten</th>
                  </tr>
                </thead>
                <tbody>
                  {(listsQ.data?.lists ?? []).map((l) => (
                    <tr key={l.id}>
                      <td>
                        <strong>{l.name}</strong>
                      </td>
                      <td>{l.member_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {libraryTab === 'sequences' && projectId ? (
        <SequenceLibraryWorkspace projectId={projectId} templates={templates} />
      ) : null}

      {libraryTab === 'templates' ? (
        <>
          {!libraryFocus ? (
          <details
            className={campStyles.disclosure}
            data-quick-send
            open={quickSendOpen}
            onToggle={(e) => setQuickSendOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary className={campStyles.disclosureSummary}>
              <span>Direct versturen</span>
              <span className={campStyles.disclosureHint}>Template → audience → verzenden</span>
            </summary>
            <div className={campStyles.disclosureBody}>
              <CampaignQuickSendPanel
                projectId={projectId}
                defaultTemplateId={quickSendTemplateId ?? undefined}
                compact
              />
            </div>
          </details>
          ) : null}

          {projectId && !libraryFocus ? (
            <EmailTemplatePerformancePanel
              projectId={projectId}
              highlightTemplateId={editingTemplateId}
            />
          ) : null}

          <ResizableWorkspace
            layout="mainSidebar"
            storageKey="mh-library-template-catalog-width"
            detailOpen={editorOpen}
            onDetailDismiss={closeTemplateEditor}
            resizerAriaLabel="Breedte catalogus aanpassen"
            defaultWidth={300}
            minWidth={240}
            maxWidth={440}
            main={
            <section className={`${campStyles.panel} ${campStyles.libraryCatalogPanel}`}>
              <div className={campStyles.panelHead}>
                <div>
                  <h2 className={campStyles.panelTitle}>Catalogus</h2>
                  <div className={campStyles.libraryKpiStrip} style={{ marginTop: '0.35rem' }}>
                    <span className={campStyles.libraryKpiChip}>
                      Totaal <strong>{templateStats.total}</strong>
                    </span>
                    <span className={campStyles.libraryKpiChip}>
                      Blokken <strong>{templateStats.blocks}</strong>
                    </span>
                    <span className={campStyles.libraryKpiChip}>
                      Legacy <strong>{templateStats.html + templateStats.text}</strong>
                    </span>
                  </div>
                </div>
                <Button type="button" onClick={startNewTemplate}>
                  + Nieuwe
                </Button>
              </div>
              <div className={campStyles.libraryCatalogBody}>
                <div className={campStyles.toolbarFilters}>
                  <input
                    type="search"
                    className={campStyles.searchInput}
                    style={{ maxWidth: 'none' }}
                    placeholder="Zoek naam of onderwerp…"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    aria-label="Templates zoeken"
                  />
                  <select
                    className={campStyles.filterSelect}
                    value={templateCategoryFilter}
                    onChange={(e) =>
                      setTemplateCategoryFilter(
                        e.target.value as 'all' | (typeof CAMPAIGN_TYPES)[number],
                      )
                    }
                  >
                    <option value="all">Alle types</option>
                    {CAMPAIGN_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {CAMPAIGN_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                  <select
                    className={campStyles.filterSelect}
                    value={templateTagFilter}
                    onChange={(e) => setTemplateTagFilter(e.target.value)}
                    aria-label="Filter op tag"
                  >
                    <option value="">Alle tags</option>
                    {commonTemplateTags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                  <label className={campStyles.filterCheck}>
                    <input
                      type="checkbox"
                      checked={showArchivedTemplates}
                      onChange={(e) => setShowArchivedTemplates(e.target.checked)}
                    />{' '}
                    Gearchiveerd
                  </label>
                </div>

                {filteredTemplates.length === 0 ? (
                  <div className={campStyles.emptyState}>
                    <p className={campStyles.emptyTitle}>Geen templates</p>
                    <p className={campStyles.emptyText}>
                      Maak een template met de blok-editor of genereer er een via AI.
                    </p>
                    <Button type="button" onClick={startNewTemplate}>
                      Nieuwe template
                    </Button>
                  </div>
                ) : (
                  <ul className={campStyles.catalogList} aria-label="Templates">
                    {filteredTemplates.map((t) => {
                      const selected = editingTemplateId === t.id;
                      const catLabel =
                        CAMPAIGN_TYPE_LABELS[t.category as keyof typeof CAMPAIGN_TYPE_LABELS] ??
                        t.category;
                      const kpi = templateKpiById.get(t.id);
                      return (
                        <li
                          key={t.id}
                          className={[
                            campStyles.catalogItem,
                            selected ? campStyles.catalogItemSelected : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <button
                            type="button"
                            className={campStyles.catalogItemMain}
                            onClick={() => openTemplateEditor(t)}
                          >
                            <span className={campStyles.catalogItemTitle}>{t.name}</span>
                            <span className={campStyles.catalogItemMeta}>
                              <span className={campStyles.catalogCategoryChip}>{catLabel}</span>
                              {renderModeBadge(t.render_mode)}
                              <span>v{t.template_version ?? 1}</span>
                              {t.archived_at ? (
                                <span className={campStyles.catalogArchiveChip}>Archief</span>
                              ) : null}
                              {kpi && kpi.sent > 0 ? (
                                <span className={campStyles.catalogKpiChip}>
                                  {kpi.sent} sends · reply {formatPct(kpi.reply_rate, 1)}
                                </span>
                              ) : null}
                            </span>
                            {(t.tags ?? []).length ? (
                              <span className={campStyles.catalogTagRow}>
                                {(t.tags ?? []).slice(0, 4).map((tag) => (
                                  <span key={tag} className={campStyles.catalogTagChip}>
                                    {tag}
                                  </span>
                                ))}
                              </span>
                            ) : null}
                            <span className={campStyles.catalogItemSubject}>{t.subject_template}</span>
                          </button>
                          <div className={campStyles.catalogItemActions}>
                            <Button
                              type="button"
                              variant="ghost"
                              className={campStyles.catalogActionBtn}
                              onClick={() => openTemplateEditor(t)}
                            >
                              Bewerk
                            </Button>
                            <Button
                              type="button"
                              className={campStyles.catalogActionBtn}
                              onClick={() => setQuickSendTemplateId(t.id)}
                            >
                              Verstuur
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
            }
            detail={
            editorOpen ? (
              <div className={campStyles.librarySticky} key="editor">
                <section className={`${campStyles.panel} ${campStyles.libraryEditorPanel}`}>
                  <form
                    onSubmit={(e: FormEvent) => {
                      e.preventDefault();
                      saveTemplate.mutate();
                    }}
                  >
                    <div className={campStyles.libraryEditorBody}>
                      <div className={campStyles.editorToolbar}>
                        <h3 className={campStyles.editorToolbarTitle}>
                          {editingTemplateId ? tplName || 'Template bewerken' : 'Nieuwe template'}
                        </h3>
                        <div className={campStyles.editorToolbarActions}>
                          {editingTemplateId && templateKpiById.get(editingTemplateId)?.sent ? (
                            <span className={campStyles.editorKpiHint}>
                              Live: {templateKpiById.get(editingTemplateId)!.sent} sends · reply{' '}
                              {formatPct(templateKpiById.get(editingTemplateId)!.reply_rate, 1)}
                            </span>
                          ) : null}
                          {editingTemplateId &&
                          editingTemplate?.render_mode === 'text' &&
                          !tplUseLegacyHtml ? (
                            <Button
                              type="button"
                              variant="ghost"
                              disabled={migrateLegacyTemplate.isPending}
                              onClick={() => migrateLegacyTemplate.mutate(editingTemplateId)}
                            >
                              Naar blokken
                            </Button>
                          ) : null}
                          {editingTemplateId ? (
                            <Button
                              type="button"
                              variant="ghost"
                              disabled={archiveTemplate.isPending}
                              onClick={() => {
                                const archived = !editingTemplate?.archived_at;
                                const ok = window.confirm(
                                  archived
                                    ? 'Template archiveren? Hij verdwijnt uit de catalogus.'
                                    : 'Template uit archief halen?',
                                );
                                if (ok) archiveTemplate.mutate(archived);
                              }}
                            >
                              {editingTemplate?.archived_at ? 'Dearchiveren' : 'Archiveren'}
                            </Button>
                          ) : null}
                          <Button type="submit" disabled={saveTemplate.isPending}>
                            {editingTemplateId ? 'Opslaan' : 'Aanmaken'}
                          </Button>
                          <Button type="button" variant="ghost" onClick={closeTemplateEditor}>
                            Sluiten
                          </Button>
                        </div>
                      </div>

                      <div className={campStyles.editorMetaGrid}>
                        <Field label="Naam">
                          <TextInput
                            value={tplName}
                            onChange={(e) => setTplName(e.target.value)}
                            required
                          />
                        </Field>
                        <Field label="Campagnetype (voor AI)">
                          <SelectInput
                            value={campaignType}
                            onChange={(e) =>
                              handleCampaignTypeChange(e.target.value as TemplateCategory)
                            }
                          >
                            {CAMPAIGN_TYPES.map((ty) => (
                              <option key={ty} value={ty}>
                                {CAMPAIGN_TYPE_LABELS[ty]}
                              </option>
                            ))}
                          </SelectInput>
                        </Field>
                        <Field
                          label="Tags"
                          hint="bijv. fase:touch-1, motion:backlink, taal:nl"
                        >
                          <TextInput
                            value={tplTagsInput}
                            onChange={(e) => setTplTagsInput(e.target.value)}
                            placeholder="fase:touch-1, taal:nl"
                          />
                        </Field>
                        <Field label="Onderwerp">
                          <TextInput
                            value={tplSubject}
                            onChange={(e) => {
                              setTplSubject(e.target.value);
                              setTplSubjectChoices([]);
                            }}
                          />
                          {tplSubjectChoices.length > 0 ? (
                            <div className={campStyles.subjectVariantRow}>
                              <span className={campStyles.subjectVariantLabel}>AI-varianten</span>
                              {tplSubjectChoices.map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  className={[
                                    campStyles.subjectVariantChip,
                                    s === tplSubject ? campStyles.subjectVariantChipActive : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  onClick={() => setTplSubject(s)}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </Field>
                      </div>

                      {projectId ? (
                        <PreviewLeadPicker
                          projectId={projectId}
                          value={previewLeadId}
                          onChange={setPreviewLeadId}
                        />
                      ) : null}

                      <EmailTemplateVariablesPanel
                        mode={tplUseLegacyHtml ? 'html' : 'blocks'}
                        content={templateVariableContent}
                        onInsert={insertTemplateVariable}
                      />

                      {!dnaReadiness.ok && editorOpen ? (
                        <div className={campStyles.brandDnaBanner} role="status">
                          Vul{' '}
                          <Link to="/bedrijfs-dna" className={campStyles.brandDnaLink}>
                            Bedrijfs-DNA
                          </Link>{' '}
                          aan voor betere AI: {dnaReadiness.missing.join(', ')}.
                        </div>
                      ) : null}

                      {!phase1Validation.ok ? (
                        <div className={campStyles.templatePhase1Banner} role="status">
                          {phase1Validation.warnings.join(' · ')}
                        </div>
                      ) : null}

                      <TemplateGovernanceBanner governance={governanceValidation} />

                      {contentScores && !tplUseLegacyHtml ? (
                        <EmailTemplateScoreChips
                          scores={contentScores}
                          showBacklinkHint={campaignType === 'backlink'}
                        />
                      ) : null}

                      <details className={campStyles.editorAdvanced}>
                        <summary>Geavanceerd</summary>
                        <label className={campStyles.legacyToggle}>
                          <input
                            type="checkbox"
                            checked={tplUseLegacyHtml}
                            onChange={(e) => {
                              setTplUseLegacyHtml(e.target.checked);
                              setEditorView('edit');
                            }}
                          />{' '}
                          Legacy HTML (niet aanbevolen)
                        </label>
                      </details>

                      {!tplUseLegacyHtml ? (
                        <div className={campStyles.editorWithStructure}>
                          <TemplateStructurePanel
                            category={campaignType}
                            onApplyStructure={() => applyBlueprintToForm(campaignType)}
                          />
                          <div className={campStyles.editorMainColumn}>
                            <div className={campStyles.editorViewBar}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <div
                                  className={campStyles.segmented}
                                  role="tablist"
                                  aria-label="Editor weergave"
                                >
                                  <button
                                    type="button"
                                    role="tab"
                                    aria-selected={editorView === 'edit'}
                                    className={[
                                      campStyles.segmentedBtn,
                                      editorView === 'edit' ? campStyles.segmentedBtnActive : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    onClick={() => setEditorView('edit')}
                                  >
                                    Inhoud
                                  </button>
                                  <button
                                    type="button"
                                    role="tab"
                                    aria-selected={editorView === 'preview'}
                                    className={[
                                      campStyles.segmentedBtn,
                                      editorView === 'preview' ? campStyles.segmentedBtnActive : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    onClick={() => setEditorView('preview')}
                                  >
                                    Voorbeeld
                                  </button>
                                </div>
                                {editorView === 'preview' ? (
                                  <div
                                    className={campStyles.segmented}
                                    role="group"
                                    aria-label="Preview viewport"
                                  >
                                    <button
                                      type="button"
                                      className={[
                                        campStyles.segmentedBtn,
                                        previewViewport === 'desktop'
                                          ? campStyles.segmentedBtnActive
                                          : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      onClick={() => setPreviewViewport('desktop')}
                                    >
                                      Desktop
                                    </button>
                                    <button
                                      type="button"
                                      className={[
                                        campStyles.segmentedBtn,
                                        previewViewport === 'mobile'
                                          ? campStyles.segmentedBtnActive
                                          : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      onClick={() => setPreviewViewport('mobile')}
                                    >
                                      Mobiel
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                              <span className={campStyles.editorViewHint}>
                                {editorView === 'preview' && previewViewport === 'mobile'
                                  ? '375px — mobiele inbox'
                                  : 'Live preview met voorbeeld-lead'}
                              </span>
                            </div>
                            <div
                              className={`${campStyles.editorCanvas} ${campStyles.editorBlocksZone}`}
                            >
                              {editorView === 'edit' ? (
                                <EmailTemplateBlockEditor
                                  blocks={tplBlocks}
                                  improvingBlockIndex={improvingBlockIndex}
                                  onImproveBlock={(index) => improveBlockAi.mutate(index)}
                                  onFocusField={(index, field) => setFocusedBlock({ index, field })}
                                  onChange={(next) => {
                                    setTplBlocks(next);
                                    const forText = next.filter(
                                      (b) =>
                                        b.type === 'divider' ||
                                        ((b.type === 'paragraph' || b.type === 'heading') &&
                                          b.text.trim()) ||
                                        (b.type === 'cta' && b.label.trim()),
                                    );
                                    if (forText.length) {
                                      try {
                                        setTplBody(
                                          blocksToPlainText(forText as EmailTemplateBlock[]),
                                        );
                                      } catch {
                                        /* incomplete blocks while typing */
                                      }
                                    }
                                  }}
                                />
                              ) : projectId ? (
                                <div className={campStyles.previewFullBleed}>
                                  <EmailTemplatePreviewPane
                                    projectId={projectId}
                                    subject={tplSubject}
                                    blocks={tplBlocks}
                                    bodyText={tplBody}
                                    leadId={sampleLeadId}
                                    presentation="inbox"
                                    viewport={previewViewport}
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : projectId ? (
                        <EmailTemplateHtmlEditor
                          projectId={projectId}
                          subject={tplSubject}
                          bodyText={tplBody}
                          bodyHtml={tplHtml}
                          leadId={sampleLeadId}
                          onBodyTextChange={setTplBody}
                          onBodyHtmlChange={setTplHtml}
                          enhanceHtml={{
                            pending: enhanceHtmlAi.isPending,
                            dnaReadiness,
                            onRun: (opts) => enhanceHtmlAi.mutate(opts),
                          }}
                        />
                      ) : null}

                      <div className={campStyles.editorFooter}>
                        {!tplUseLegacyHtml ? (
                          <EmailTemplateAiWizard
                            campaignType={campaignType}
                            onGenerate={runAiFromWizard}
                            isPending={aiTplFromDna.isPending}
                            disabled={!projectId}
                          />
                        ) : null}
                        <div className={campStyles.editorAiRow}>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={!sampleLeadId || aiTplSubjects.isPending}
                            onClick={() => aiTplSubjects.mutate()}
                          >
                            {aiTplSubjects.isPending ? 'AI…' : 'AI onderwerp (2×)'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={!inboxTestReady || inboxTestSend.isPending}
                            onClick={() => inboxTestSend.mutate()}
                          >
                            {inboxTestSend.isPending ? 'Versturen…' : 'Test naar mijn inbox'}
                          </Button>
                          {aiTplFromDna.isError ? (
                            <span className={styles.mutedSmall}>
                              {aiTplFromDna.error instanceof ApiError &&
                              aiTplFromDna.error.code === 'AI_UNAVAILABLE'
                                ? 'LLM niet geconfigureerd.'
                                : aiTplFromDna.error instanceof ApiError &&
                                    aiTplFromDna.error.code === 'BRAND_DNA_INCOMPLETE'
                                  ? aiTplFromDna.error.message
                                  : 'AI-template mislukt.'}
                            </span>
                          ) : null}
                          {tplAiWarning ? (
                            <span className={styles.mutedSmall}>Validatie: {tplAiWarning}</span>
                          ) : null}
                          {saveTemplate.isError ? (
                            <span className={styles.mutedSmall}>{saveTemplate.error.message}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </form>
                </section>
              </div>
            ) : null
            }
          />
        </>
      ) : null}

      {libraryTab === 'suppressions' ? (
        <section className={campStyles.panel}>
          <div className={campStyles.panelHead}>
            <h2 className={campStyles.panelTitle}>Suppressies (DNC)</h2>
          </div>
          <div className={campStyles.panelBody}>
            <form
              className={styles.formGrid}
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                addSuppression.mutate();
              }}
            >
              <Field label="E-mail">
                <TextInput
                  type="email"
                  value={suppEmail}
                  onChange={(e) => setSuppEmail(e.target.value)}
                  placeholder="optioneel"
                />
              </Field>
              <Field label="Domein">
                <TextInput
                  value={suppDomain}
                  onChange={(e) => setSuppDomain(e.target.value)}
                  placeholder="example.com"
                />
              </Field>
              <Button type="submit">Toevoegen</Button>
            </form>
            <div className={campStyles.tableWrap}>
              <table className={campStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Adres / domein</th>
                    <th>Reden</th>
                  </tr>
                </thead>
                <tbody>
                  {(suppressionsQ.data?.suppressions ?? []).slice(0, 50).map((s) => (
                    <tr key={s.id}>
                      <td>{s.email_normalized ?? s.domain_normalized}</td>
                      <td>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
